import { Calendar, Trophy, Users, X } from 'lucide-react';
import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router';
import { excludeNominee, getDraws, getLotteryDetails, getNomineeApplications } from '@/api/Lottery';
import { Layout } from '@/components/Layout';
import TabItem from '@/components/Tabs/TabItem';
import TabList from '@/components/Tabs/TabList';
import Card from '@/components/UI/Card/index.tsx';
import StatCard from '@/components/UI/Card/StatCard';
import { PageFooterPagination } from '@/components/UI/Pagination/PageFooterPagination';
import type { Lottery } from '@/types/Lottery';
import { formatPhoneNumber } from '@/utils';
import DrawList from './DrawList';
import LotteryInformation from './LotteryInformation';

const LotteryDetails: React.FC = () => {
  const { lotteryId } = useParams<{ lotteryId: string }>();
  const [lottery, setLottery] = useState<Lottery | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [tabIndex, setTabIndex] = useState(0);
  const [nominees, setNominees] = useState<any[]>([]);
  const [isNomineesLoading, setIsNomineesLoading] = useState(false);
  const [nomineesError, setNomineesError] = useState<string | null>(null);
  const [nomineeFilters, _setNomineeFilters] = useState({
    email: '',
    name: '',
    address: '',
    status: '',
  });
  const [pendingFilters, setPendingFilters] = useState(nomineeFilters);
  const [drawOptions, setDrawOptions] = useState<any[]>([]);
  const [selectedDrawId, setSelectedDrawId] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [totalCount, setTotalCount] = useState(0);
  const _searchTriggerRef = useRef(0);
  const [_searchTrigger, _setSearchTrigger] = useState(0);
  const [searchParams, setSearchParams] = useState({
    email: '',
    name: '',
    address: '',
    status: '',
    drawId: '',
  });
  const [showExcludeModal, setShowExcludeModal] = useState(false);
  const [selectedNominee, setSelectedNominee] = useState<any>(null);
  const [excludeRemarks, setExcludeRemarks] = useState('');

  useEffect(() => {
    if (lotteryId) {
      const fetchLotteryDetails = async () => {
        try {
          const response = await getLotteryDetails(lotteryId);
          setLottery(response.data.data);
        } catch (error) {
          console.error('Failed to fetch lottery details', error);
        } finally {
          setIsLoading(false);
        }
      };
      fetchLotteryDetails();
    }
  }, [lotteryId]);

  useEffect(() => {
    if (tabIndex === 1 && lotteryId) {
      setIsNomineesLoading(true);
      setNomineesError(null);
      getNomineeApplications(lotteryId, {
        email: searchParams.email,
        name: searchParams.name,
        address: searchParams.address,
        status: searchParams.status,
        drawId: searchParams.status === 'WINNER' ? searchParams.drawId : undefined,
        page,
        perPage: pageSize,
      })
        .then((res) => {
          // Extract nominees from data array and add status based on drawResults
          const nomineesWithStatus = (res.data.data || []).map((nominee: any) => {
            return {
              ...nominee,
              status: nominee.status,
            };
          });

          setNominees(nomineesWithStatus);
          // Extract total count from meta.count
          setTotalCount(res.data.meta?.count || 0);
        })
        .catch((_err) => {
          setNomineesError('Failed to load nominees');
        })
        .finally(() => setIsNomineesLoading(false));
    }
  }, [tabIndex, lotteryId, page, pageSize, searchParams]);

  useEffect(() => {
    setPage(1);
  }, []);

  useEffect(() => {
    if (pendingFilters.status === 'WINNER' && lotteryId) {
      getDraws(lotteryId).then((res) => {
        setDrawOptions(res.data.data || []);
      });
    } else {
      setDrawOptions([]);
      setSelectedDrawId('');
    }
  }, [pendingFilters.status, lotteryId]);

  const handleUpdate = (updatedLottery: Partial<Lottery>) => {
    setLottery((prevLottery) => {
      if (!prevLottery) return null;
      return { ...prevLottery, ...updatedLottery };
    });
  };

  // Only update searchParams and trigger API when Search is clicked
  const handleSearch = () => {
    setSearchParams({
      email: pendingFilters.email,
      name: pendingFilters.name,
      address: pendingFilters.address,
      status: pendingFilters.status,
      drawId: selectedDrawId,
    });
    // Reset page to 1 when searching
    setPage(1);
  };

  const handleExcludeClick = (nominee: any) => {
    setSelectedNominee(nominee);
    setExcludeRemarks('');
    setShowExcludeModal(true);
  };

  const handleExcludeCancel = () => {
    setShowExcludeModal(false);
    setSelectedNominee(null);
    setExcludeRemarks('');
  };

  const handleExcludeDone = async () => {
    if (!selectedNominee || !excludeRemarks.trim() || !lotteryId) {
      return;
    }

    try {
      await excludeNominee(lotteryId, selectedNominee.id, {
        remarks: excludeRemarks,
      });

      // Close modal and reset state
      setShowExcludeModal(false);
      setSelectedNominee(null);
      setExcludeRemarks('');

      // Refresh the nominees list to reflect the exclusion
      if (tabIndex === 1) {
        setIsNomineesLoading(true);
        setNomineesError(null);
        getNomineeApplications(lotteryId, {
          email: searchParams.email,
          name: searchParams.name,
          address: searchParams.address,
          status: searchParams.status,
          drawId: searchParams.status === 'WINNER' ? searchParams.drawId : undefined,
          page,
          perPage: pageSize,
        })
          .then((res) => {
            setNominees(res.data.data);
            setTotalCount(res.data.meta?.count || 0);
          })
          .catch((_err) => {
            setNomineesError('Failed to load nominees');
          })
          .finally(() => setIsNomineesLoading(false));
      }
    } catch (error) {
      console.error('Failed to exclude nominee:', error);
      // You might want to show an error message to the user here
    }
  };

  if (isLoading) {
    return <p>Loading...</p>;
  }

  if (!lottery) {
    return <p>Lottery not found.</p>;
  }

  // Summary stats
  const totalNominees = typeof lottery.totalNominees === 'number' ? lottery.totalNominees : nominees.length;
  const totalDraws = typeof lottery.totalDraws === 'number' ? lottery.totalDraws : lottery.draws ? lottery.draws.length : 0;
  const totalWinners =
    typeof lottery.totalWinners === 'number'
      ? lottery.totalWinners
      : lottery.draws
        ? lottery.draws.reduce((acc, draw) => acc + (draw.applicants?.filter((a) => a.isWinner).length || 0), 0)
        : 0;

  return (
    <Layout>
      <div className='space-y-6'>
        {/* Top summary section */}
        <div className='flex flex-col gap-4 md:flex-row md:gap-6'>
          <div className='flex-1'>
            <StatCard title='Total Applications' stat={totalNominees} icon={<Users size={20} />} />
          </div>
          <div className='flex-1'>
            <StatCard title='Total Draws' stat={totalDraws} icon={<Calendar size={20} />} />
          </div>
          <div className='flex-1'>
            <StatCard title='Total Winners' stat={totalWinners} icon={<Trophy size={20} />} />
          </div>
        </div>

        {/* Tab navigation */}
        <div className='mt-4'>
          <TabList defaultIndex={tabIndex} index={tabIndex} onChange={setTabIndex}>
            <TabItem index={0} label='Overview'>
              <Card title='Lottery Information'>
                <LotteryInformation lottery={lottery} onUpdate={handleUpdate} />
              </Card>
            </TabItem>
            <TabItem index={1} label='Applications'>
              <Card title='Applications'>
                {isNomineesLoading && <p className='text-gray-500'>Loading nominees...</p>}
                {nomineesError && <p className='text-red-500'>{nomineesError}</p>}

                <div className='mb-6 bg-gray-50 border border-gray-200 rounded-lg p-4 shadow-sm'>
                  <div className='flex flex-col sm:flex-row gap-4 items-end'>
                    <div className='flex-1 min-w-[150px]'>
                      <label className='block text-xs font-medium text-gray-500 mb-1'>Email</label>
                      <input
                        type='text'
                        value={pendingFilters.email}
                        onChange={(e) =>
                          setPendingFilters((f) => ({
                            ...f,
                            email: e.target.value,
                          }))
                        }
                        className='border border-gray-300 rounded px-3 py-2 text-sm w-full focus:ring-2 focus:ring-primaryColor-normal'
                        placeholder='Filter by email'
                      />
                    </div>
                    <div className='flex-1 min-w-[150px]'>
                      <label className='block text-xs font-medium text-gray-500 mb-1'>Name</label>
                      <input
                        type='text'
                        value={pendingFilters.name}
                        onChange={(e) =>
                          setPendingFilters((f) => ({
                            ...f,
                            name: e.target.value,
                          }))
                        }
                        className='border border-gray-300 rounded px-3 py-2 text-sm w-full focus:ring-2 focus:ring-primaryColor-normal'
                        placeholder='Filter by name'
                      />
                    </div>
                    <div className='flex-1 min-w-[150px]'>
                      <label className='block text-xs font-medium text-gray-500 mb-1'>Address</label>
                      <input
                        type='text'
                        value={pendingFilters.address}
                        onChange={(e) =>
                          setPendingFilters((f) => ({
                            ...f,
                            address: e.target.value,
                          }))
                        }
                        className='border border-gray-300 rounded px-3 py-2 text-sm w-full focus:ring-2 focus:ring-primaryColor-normal'
                        placeholder='Filter by address'
                      />
                    </div>
                    <div className='flex-1 min-w-[150px]'>
                      <label className='block text-xs font-medium text-gray-500 mb-1'>Status</label>
                      <select
                        value={pendingFilters.status}
                        onChange={(e) =>
                          setPendingFilters((f) => ({
                            ...f,
                            status: e.target.value,
                          }))
                        }
                        className='border border-gray-300 rounded px-3 py-2 text-sm w-full focus:ring-2 focus:ring-primaryColor-normal'
                      >
                        <option value=''>ALL</option>
                        <option value='NOT_SELECTED'>NOT SELECTED</option>
                        <option value='WINNER'>WINNER</option>
                        <option value='EXCLUDED'>EXCLUDED</option>
                      </select>
                    </div>
                    {pendingFilters.status === 'WINNER' && (
                      <div className='flex-1 min-w-[150px]'>
                        <label className='block text-xs font-medium text-gray-500 mb-1'>Draw</label>
                        <select
                          value={selectedDrawId}
                          onChange={(e) => setSelectedDrawId(e.target.value)}
                          className='border border-gray-300 rounded px-3 py-2 text-sm w-full focus:ring-2 focus:ring-primaryColor-normal'
                        >
                          <option value=''>All Draws</option>
                          {drawOptions.map((draw) => (
                            <option key={draw.id} value={draw.id}>
                              {draw.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                    <button
                      type='button'
                      className='ml-2 px-4 py-2 bg-primaryColor-normal text-white rounded hover:bg-primaryColor-dark text-xs'
                      onClick={handleSearch}
                    >
                      Search
                    </button>
                    <button
                      type='button'
                      className='ml-2 px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 text-xs'
                      onClick={() => {
                        setPendingFilters({
                          email: '',
                          name: '',
                          address: '',
                          status: '',
                        });
                        setSearchParams({
                          email: '',
                          name: '',
                          address: '',
                          status: '',
                          drawId: '',
                        });
                        setSelectedDrawId('');
                        setPage(1);
                      }}
                    >
                      Clear Filters
                    </button>
                  </div>
                </div>
                <div className='overflow-x-auto'>
                  <table className='w-full divide-y divide-gray-200'>
                    <thead className='bg-gray-50'>
                      <tr>
                        <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>Name</th>
                        <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>Email</th>
                        <th className='px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32'>
                          Phone Number
                        </th>
                        <th className='px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-40'>Address</th>
                        <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>Status</th>
                        <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[400px]'>
                          Remarks
                        </th>
                        <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>Submitted</th>
                        <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>Action</th>
                      </tr>
                    </thead>
                    <tbody className='bg-white divide-y divide-gray-200'>
                      {nominees.length === 0 ? (
                        <tr>
                          <td className='px-6 py-4 whitespace-nowrap' />
                          <td className='px-6 py-4 whitespace-nowrap' />
                          <td className='px-4 py-4 whitespace-nowrap w-32' />
                          <td className='px-4 py-4 whitespace-nowrap w-40' />
                          <td className='px-6 py-4 whitespace-nowrap' />
                          <td className='px-6 py-4 whitespace-nowrap min-w-[400px]' />
                          <td className='px-6 py-4 whitespace-nowrap' />
                          <td className='px-6 py-4 whitespace-nowrap text-center' />
                        </tr>
                      ) : (
                        nominees.map((nominee) => (
                          <tr key={nominee.id}>
                            <td className='px-6 py-4 whitespace-nowrap'>{nominee.name}</td>
                            <td className='px-6 py-4 whitespace-nowrap'>{nominee.email}</td>
                            <td className='px-4 py-4 whitespace-nowrap w-32'>{formatPhoneNumber(nominee.contactInfo || '')}</td>
                            <td className='px-4 py-4 whitespace-nowrap w-40'>{nominee.address}</td>
                            <td className='px-6 py-4'>
                              <span
                                className={`px-2 py-1 text-xs rounded-full w-fit ${
                                  nominee.status === 'WINNER'
                                    ? 'bg-green-100 text-green-800'
                                    : nominee.status === 'EXCLUDED'
                                      ? 'bg-red-100 text-red-800'
                                      : 'bg-gray-100 text-gray-800'
                                }`}
                              >
                                {nominee.status}
                              </span>
                            </td>
                            <td className='px-6 py-4 min-w-[400px]'>
                              {nominee.remarks ? (
                                <div className='text-xs text-gray-600 break-words whitespace-normal'>{nominee.remarks}</div>
                              ) : (
                                <span className='text-gray-400'>-</span>
                              )}
                            </td>
                            <td className='px-6 py-4 whitespace-nowrap'>
                              {nominee.createdAt ? new Date(nominee.createdAt).toLocaleString() : '-'}
                            </td>
                            <td className='px-6 py-4 whitespace-nowrap text-center'>
                              <button
                                type='button'
                                className='p-2 rounded hover:bg-red-100'
                                title='Exclude Nominee'
                                onClick={() => handleExcludeClick(nominee)}
                              >
                                <X size={18} className='text-red-600' />
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
                {/* Pagination controls */}
                <div className='mt-4'>
                  <div className='flex items-center justify-between mb-4'>
                    <div className='text-sm text-gray-600 pl-5'>
                      {totalCount > 0 ? `${Math.min(page * pageSize, totalCount)} of ${totalCount} applications` : 'No application found'}
                    </div>
                    <div className='flex items-center gap-2'>
                      <span className='text-sm text-gray-600'>Items per page:</span>
                      <select
                        value={pageSize}
                        onChange={(e) => {
                          setPageSize(Number(e.target.value));
                          setPage(1);
                        }}
                        className='border border-gray-300 rounded px-2 py-1 text-sm bg-white'
                      >
                        {[5, 10, 20, 50].map((size) => (
                          <option key={size} value={size}>
                            {size}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  {/* Force pagination to show for testing */}
                  <PageFooterPagination page={page} totalPages={Math.max(1, Math.ceil(totalCount / pageSize))} setPage={setPage} />
                </div>
              </Card>
            </TabItem>
            <TabItem index={2} label='Draws'>
              <Card title='Draws'>
                <DrawList lotteryId={lotteryId || ''} lottery={lottery} />
              </Card>
            </TabItem>
          </TabList>
        </div>
      </div>

      {/* Exclude Modal */}
      {showExcludeModal && (
        <div className='fixed inset-0 bg-black/50 flex items-center justify-center z-50'>
          <div className='bg-white rounded-lg shadow-xl max-w-md w-full mx-4'>
            <div className='p-6'>
              <div className='flex items-center justify-between mb-4'>
                <h3 className='text-lg font-semibold text-gray-900'>Exclude Nominee</h3>
                <button onClick={handleExcludeCancel} className='text-gray-400 hover:text-gray-600'>
                  <X size={20} />
                </button>
              </div>

              <div className='mb-4'>
                <p className='text-sm text-gray-600 mb-2'>
                  Excluding: <span className='font-medium'>{selectedNominee?.name}</span>
                </p>
                <p className='text-sm text-gray-600 mb-4'>
                  Email: <span className='font-medium'>{selectedNominee?.email}</span>
                </p>
              </div>

              <div className='mb-6'>
                <label htmlFor='excludeRemarks' className='block text-sm font-medium text-gray-700 mb-2'>
                  Remarks (Required)
                </label>
                <textarea
                  id='excludeRemarks'
                  value={excludeRemarks}
                  onChange={(e) => setExcludeRemarks(e.target.value)}
                  className='w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-primaryColor-normal focus:border-transparent'
                  rows={4}
                  placeholder='Please provide a reason for excluding this nominee...'
                  required
                />
              </div>

              <div className='flex justify-end gap-3'>
                <button
                  type='button'
                  onClick={handleExcludeCancel}
                  className='px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-hidden focus:ring-2 focus:ring-gray-500'
                >
                  Cancel
                </button>
                <button
                  type='button'
                  onClick={handleExcludeDone}
                  disabled={!excludeRemarks.trim()}
                  className='px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-hidden focus:ring-2 focus:ring-red-500 disabled:bg-gray-300 disabled:cursor-not-allowed'
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default LotteryDetails;
